import { Link } from "react-router-dom";
import { Calendar, User, ArrowRight, Heart, Home, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import heroImage from "@/assets/blog-divorce-texas.jpg";
import { generateArticleSchema } from "@/lib/seo-schemas";

const DivorceTexas = () => {
  const schema = generateArticleSchema(
    "Divorce in Texas: What You Need to Know",
    "Understanding Texas divorce laws, property division, and child custody considerations.",
    "2024-12-10",
    "2024-12-10",
    "https://danielalbertlaw.com/blog/divorce-texas-guide"
  );

  return (
    <div className="min-h-screen">
      <SEO
        title="Divorce in Texas: What You Need to Know | Daniel Albert Law"
        description="Understanding Texas divorce laws, property division, and child custody considerations. Expert family law attorney guidance for Houston residents."
        keywords="Texas divorce, community property, child custody Texas, divorce lawyer Houston, family law"
        schema={schema}
      />
      
      <Header />
      
      <Breadcrumbs
        items={[
          { label: "Blog", href: "/blog" },
          { label: "Divorce in Texas: What You Need to Know" }
        ]}
      />

      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl text-primary-foreground">
            <div className="flex items-center gap-4 mb-4 text-sm">
              <span className="inline-block bg-accent/20 text-accent px-3 py-1 rounded-full font-semibold">
                Family Law
              </span>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>December 10, 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>Daniel Albert</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 drop-shadow-lg">
              Divorce in Texas: What You Need to Know
            </h1>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="prose prose-lg max-w-none mb-12">
              <p className="text-xl text-muted-foreground leading-relaxed">
                Divorce is one of life's most challenging experiences, both emotionally and legally. Texas has specific laws governing divorce that differ from other states, particularly regarding property division and child custody. Understanding these laws can help you navigate the process more effectively and protect your rights and interests.
              </p>
            </div>

            {/* Quick Facts Card */}
            <Card className="mb-12 bg-gradient-subtle">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6">Texas Divorce Basics</h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Texas is a "no-fault" divorce state—you don't need to prove wrongdoing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Minimum 60-day waiting period from filing to finalization</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Texas is a community property state</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Courts prioritize the "best interest of the child" in custody decisions</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Main Content Sections */}
            <div className="space-y-12">
              <section>
                <h2 className="text-3xl font-bold mb-6">Residency Requirements</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Before you can file for divorce in Texas, you must meet the following residency requirements:
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Either you or your spouse must have been a resident of Texas for at least <strong>6 months</strong> before filing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>You must have lived in the county where you're filing for at least <strong>90 days</strong></span>
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Grounds for Divorce in Texas</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Texas recognizes both "no-fault" and "fault" grounds for divorce:
                </p>
                
                <div className="space-y-6">
                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3">No-Fault Divorce</h3>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span><strong>Insupportability:</strong> The marriage has become insupportable due to discord or conflict that destroys the legitimate ends of the marriage with no reasonable expectation of reconciliation. This is the most common ground for divorce in Texas.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span><strong>Living Apart:</strong> The spouses have lived apart without cohabitation for at least three years.</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3">Fault-Based Grounds</h3>
                    <p className="text-muted-foreground mb-3">While less common, fault grounds can affect property division and alimony:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Cruelty (physical or mental)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Adultery</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Conviction of a felony</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Abandonment for at least one year</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Confinement in a mental hospital</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
                  <Home className="h-8 w-8 text-accent" />
                  Property Division: Community Property vs. Separate Property
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Texas is one of nine community property states. Understanding the difference between community and separate property is crucial:
                </p>

                <div className="space-y-6">
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-2xl font-semibold mb-3">Community Property</h3>
                    <p className="text-lg text-muted-foreground mb-3">
                      All assets and debts acquired during the marriage are presumed to be community property and subject to division, including:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Income earned by either spouse during marriage</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Property purchased with community funds</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Retirement benefits accrued during marriage</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Debts incurred during marriage</span>
                      </li>
                    </ul>
                    <p className="text-muted-foreground mt-3">
                      <strong>Important:</strong> Community property is divided in a "just and right" manner, which doesn't necessarily mean 50/50. Courts consider factors like each spouse's earning capacity, fault in the breakup, education, and more.
                    </p>
                  </div>

                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-2xl font-semibold mb-3">Separate Property</h3>
                    <p className="text-lg text-muted-foreground mb-3">
                      Separate property belongs to one spouse alone and is not divided in divorce:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Property owned before marriage</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Inheritances received by one spouse</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Gifts given to one spouse individually</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Personal injury settlements (except for lost wages)</span>
                      </li>
                    </ul>
                    <p className="text-muted-foreground mt-3">
                      <strong>Note:</strong> The burden of proof is on the spouse claiming property is separate. Without clear evidence, it will be presumed community property.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
                  <Users className="h-8 w-8 text-accent" />
                  Child Custody and Support
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  In Texas, custody is referred to as "conservatorship." Courts determine custody based on the best interest of the child.
                </p>

                <div className="space-y-6">
                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3">Types of Conservatorship</h3>
                    <ul className="space-y-3 text-muted-foreground">
                      <li className="flex items-start gap-3">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <div>
                          <strong>Joint Managing Conservatorship (JMC):</strong> Both parents share decision-making rights, though the child may primarily reside with one parent. This is the most common arrangement and is presumed to be in the child's best interest unless there's evidence of family violence or other concerns.
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <div>
                          <strong>Sole Managing Conservatorship (SMC):</strong> One parent has the exclusive right to make major decisions about the child. The other parent may have limited visitation rights or no rights at all.
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <div>
                          <strong>Possessory Conservatorship:</strong> One parent has the right to physical possession of the child during specific times, while the other parent makes most decisions.
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-gradient-subtle p-6 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-3">Child Support</h3>
                    <p className="text-muted-foreground mb-3">
                      In Texas, child support is calculated based on a percentage of the non-custodial parent's net monthly income:
                    </p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>1 child: 20% of net income</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>2 children: 25% of net income</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>3 children: 30% of net income</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>4+ children: increases by 5% per additional child</span>
                      </li>
                    </ul>
                    <p className="text-muted-foreground mt-3">
                      These percentages apply to the first $9,200 of monthly net income. Courts may deviate from these guidelines based on the specific circumstances of the case.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6 flex items-center gap-2">
                  <Heart className="h-8 w-8 text-accent" />
                  Spousal Support (Alimony)
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Texas law refers to alimony as "spousal maintenance." Unlike community property division, spousal maintenance is limited and only available under specific circumstances:
                </p>
                <div className="space-y-4">
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">Eligibility Requirements</h3>
                    <p className="text-muted-foreground mb-3">The marriage must have lasted at least 10 years, AND the spouse seeking support must:</p>
                    <ul className="space-y-2 text-muted-foreground">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Lack sufficient property to provide for minimum reasonable needs, AND</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                        <span>Be unable to support themselves through employment due to disability, caring for a disabled child, or lack of earning ability</span>
                      </li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">Duration and Amount</h3>
                    <p className="text-muted-foreground">
                      Spousal maintenance is typically limited to the lesser of $5,000 per month or 20% of the paying spouse's gross monthly income. Duration depends on the length of marriage and specific circumstances.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">The Divorce Process Timeline</h2>
                <div className="space-y-4">
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">1. Filing the Petition (Day 1)</h3>
                    <p className="text-muted-foreground">
                      One spouse files a "Petition for Divorce" with the court. The other spouse is served and has the opportunity to respond.
                    </p>
                  </div>
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">2. Temporary Orders (if needed)</h3>
                    <p className="text-muted-foreground">
                      Courts can issue temporary orders for child custody, support, use of property, and restraining orders during the divorce process.
                    </p>
                  </div>
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">3. Discovery</h3>
                    <p className="text-muted-foreground">
                      Both parties exchange financial information, documents, and evidence relevant to property division and custody.
                    </p>
                  </div>
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">4. Negotiation and Mediation</h3>
                    <p className="text-muted-foreground">
                      Most divorces are settled through negotiation or mediation rather than going to trial.
                    </p>
                  </div>
                  <div className="border-l-4 border-accent pl-6 py-2">
                    <h3 className="text-xl font-semibold mb-2">5. Final Decree (Minimum 60 days)</h3>
                    <p className="text-muted-foreground">
                      After the 60-day waiting period, if an agreement is reached or a trial concludes, the court issues a Final Decree of Divorce, officially ending the marriage.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6">Why You Need a Family Law Attorney</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  While Texas allows pro se (self-representation) divorce, having an experienced attorney significantly improves outcomes:
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Protects your financial interests in property division</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Ensures fair child custody arrangements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Navigates complex legal procedures and deadlines</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Provides objective advice during an emotional time</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <ArrowRight className="h-5 w-5 text-accent flex-shrink-0 mt-1" />
                    <span>Handles all negotiations and court appearances</span>
                  </li>
                </ul>
              </section>
            </div>

            {/* Related Services */}
            <Card className="mt-12 bg-primary text-primary-foreground">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-4">Compassionate Divorce Representation in Houston</h2>
                <p className="text-lg mb-6 opacity-95">
                  At Daniel Albert Law Firm, we understand that divorce is more than just a legal process—it's a life transition. We provide compassionate, personalized representation to protect your rights and help you move forward. Our bilingual team serves clients in English, Spanish, and Vietnamese.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="outline" className="border-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                    <Link to="/practice-areas/family-law/divorce">
                      Learn More About Divorce Services
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </article>

      <CTASection
        title="Going Through a Divorce?"
        description="Contact us today for compassionate, experienced legal representation. We're here to guide you through every step of the process."
      />

      <Footer />
    </div>
  );
};

export default DivorceTexas;